/**
 * Declarative config of the « Mes primes » list (`extranet.premiumsListCard`,
 * fiches 22749 / 22988): stable column keys, the toolbar (server-side search
 * `filtre` + situation select `situation`, built with the shared
 * `buildListToolbar`), the per-row « Documents » action, the situation badge
 * map and the DataList config builder. Every label / tooltip / placeholder
 * lives here (French), never in JSX, so the tenant rules can rename / hide /
 * reorder them.
 *
 * Importing this file registers two DataList cell renderers (situation badge,
 * « Documents » button) with the singleton `dataListWorkflowRegistry`.
 */

import { FileText } from 'lucide-react';

import { Button } from '@components/base/actions';
import { Icon } from '@components/base/display';
import {
  dataListWorkflowRegistry,
  type ColumnConfig,
  type DataListConfig,
} from '@components/integrated/data-list';
import type { ActionsConfig, CustomActionConfig } from '@components/integrated/entity-form';

import type { SelOption } from '../../api/listFilters';
import type { Premium } from '../../api/premiumsService';
import {
  EMPTY_STATE_TEXT,
  EXTRANET_DATE_RENDERER,
  PREMIUM_SITUATION_BADGE,
  registerStatusBadgeRenderer,
} from '../shared';
import { buildListToolbar, type ListToolbarSpec } from '../shared/listToolbarConfig';

/** Registered id of the list card. */
export const PREMIUMS_LIST_CARD_ID = 'extranet.premiumsListCard';

/**
 * Card-level texts. No base title: the page header carries « Mes primes »
 * and {@link premiumsCountSubtitle} (a `setTitle` rule adds a card title).
 */
export const PREMIUMS_LIST_TEXT = {
  /** Search placeholder (22988 mockup). */
  searchPlaceholder: 'Rechercher par réf, produit, assuré…',
  /** Label of the situation select (its placeholder too). */
  situationFilterLabel: 'Situation',
  /** « All » option of the situation select — no `situation` sent. */
  allSituations: 'Toutes situations',
  documentsTooltip: 'Voir les documents de la prime',
} as const;

/**
 * « {n} primes » — page header subtitle of « Mes primes » (French: 0 and 1
 * are singular). Counts the rows of the current search / situation.
 */
export function premiumsCountSubtitle(count: number): string {
  return `${String(count)} ${count > 1 ? 'primes' : 'prime'}`;
}

// ---------------------------------------------------------------------------
// Situation badge (QUI_SITUATION shown verbatim; « Payée… » green, « Impayée » red)
// ---------------------------------------------------------------------------

/** Renderer key of the `situation` column (shared `PREMIUM_SITUATION_BADGE`). */
export const PREMIUM_SITUATION_RENDERER = registerStatusBadgeRenderer(
  'extranet-premiumSituation',
  PREMIUM_SITUATION_BADGE,
);

// ---------------------------------------------------------------------------
// Per-row « Documents » action (action key `documents`)
// ---------------------------------------------------------------------------

/** Stable action key — target of `setActionLabel` / `hideAction`. */
export const PREMIUM_DOCUMENTS_ACTION_KEY = 'documents';

/**
 * The « Documents » button (no count: « Documents (n) » is unspecified).
 * The card wires its click; `handler` only names it.
 */
export const PREMIUM_DOCUMENTS_ACTION = {
  id: PREMIUM_DOCUMENTS_ACTION_KEY,
  handler: 'extranet-premiumDocuments',
  label: 'Documents',
  variant: 'secondary',
  size: 'sm',
} satisfies CustomActionConfig;

/** Published action surface of the card. */
export const PREMIUMS_LIST_ACTIONS: ActionsConfig = { custom: [PREMIUM_DOCUMENTS_ACTION] };

/** The « Documents » button as resolved for this render (overrides applied). */
export interface ResolvedPremiumAction {
  label: string;
  visible: boolean;
}

/** Per-row context carried under `__ctx` (fresh closures per render). */
export interface PremiumRowContext {
  documentsAction: ResolvedPremiumAction;
  onOpenDocuments: () => void;
}

/** A list row: the premium + its per-row context. */
export type PremiumListRow = Premium & {
  __ctx: PremiumRowContext;
};

function getPremiumRowContext(row: Record<string, unknown>): PremiumRowContext | undefined {
  const ctx = row.__ctx;
  return typeof ctx === 'object' && ctx !== null ? (ctx as PremiumRowContext) : undefined;
}

/** Renderer key of the `actions` column. */
export const PREMIUM_DOCUMENTS_RENDERER = 'extranet-premiumDocumentsAction';

dataListWorkflowRegistry.registerCellRenderer(PREMIUM_DOCUMENTS_RENDERER, (_value, row) => {
  const ctx = getPremiumRowContext(row);
  if (!ctx?.documentsAction.visible) return null;
  return (
    <Button
      variant={PREMIUM_DOCUMENTS_ACTION.variant}
      size={PREMIUM_DOCUMENTS_ACTION.size}
      leftIcon={<Icon icon={FileText} size="sm" />}
      title={PREMIUMS_LIST_TEXT.documentsTooltip}
      data-action-key={PREMIUM_DOCUMENTS_ACTION_KEY}
      onClick={(event) => {
        event.stopPropagation();
        ctx.onOpenDocuments();
      }}
    >
      {ctx.documentsAction.label}
    </Button>
  );
});

// ---------------------------------------------------------------------------
// Columns (design / fiche order)
// ---------------------------------------------------------------------------

/** Stable column keys. */
export type PremiumsListColumnKey =
  | 'assure'
  | 'produit'
  | 'numeroContrat'
  | 'reference'
  | 'periodeDu'
  | 'periodeAu'
  | 'ttc'
  | 'situation'
  | 'actions';

export const PREMIUMS_LIST_COLUMNS: Readonly<Record<PremiumsListColumnKey, ColumnConfig>> = {
  assure: {
    label: 'Assuré',
    order: 1,
    type: 'text',
    className: 'font-medium',
    sortable: true,
  },
  produit: {
    label: 'Produit',
    order: 2,
    type: 'text',
    className: 'text-secondary-500',
    sortable: true,
  },
  numeroContrat: {
    label: 'N° contrat',
    order: 3,
    type: 'text',
    className: 'font-mono text-xs',
    sortable: true,
  },
  reference: {
    label: 'Réf. prime',
    order: 4,
    type: 'text',
    className: 'font-mono text-xs',
    sortable: true,
  },
  periodeDu: {
    label: 'Période du',
    order: 5,
    type: 'custom',
    typeOptions: { renderer: EXTRANET_DATE_RENDERER },
    sortable: true,
  },
  periodeAu: {
    label: 'Période au',
    order: 6,
    type: 'custom',
    typeOptions: { renderer: EXTRANET_DATE_RENDERER },
    sortable: true,
  },
  ttc: {
    label: 'TTC',
    headerTooltip: 'Montant TTC',
    order: 7,
    type: 'currency',
    typeOptions: { currency: 'EUR', locale: 'fr-FR' },
    align: 'right',
    className: 'font-semibold',
    sortable: true,
  },
  situation: {
    label: 'Statut',
    headerTooltip: 'Situation de la prime',
    order: 8,
    type: 'custom',
    typeOptions: { renderer: PREMIUM_SITUATION_RENDERER },
    align: 'center',
    sortable: true,
  },
  actions: {
    label: '',
    order: 9,
    type: 'custom',
    typeOptions: { renderer: PREMIUM_DOCUMENTS_RENDERER },
    align: 'right',
    sortable: false,
  },
};

// ---------------------------------------------------------------------------
// Toolbar: server-side search (`filtre`) + situation select (`situation`)
// ---------------------------------------------------------------------------

/** Stable id of the situation filter — target of the `*Filter` rule arms. */
export const PREMIUM_SITUATION_FILTER_ID = 'situation';

/**
 * The toolbar of the list (fiche 22988): the search box and the situation
 * select — « Toutes situations » (default, `situation` not sent) then the
 * `WS2_EA_SEL_STATUT_PRIME` options in API order. The page maps the
 * reported search to `filtre` and the select to `situation`
 * (`buildPremiumsListParams`).
 */
export function premiumsListToolbarSpec(situationOptions: readonly SelOption[]): ListToolbarSpec {
  return {
    search: { placeholder: PREMIUMS_LIST_TEXT.searchPlaceholder },
    status: {
      id: PREMIUM_SITUATION_FILTER_ID,
      label: PREMIUMS_LIST_TEXT.situationFilterLabel,
      allLabel: PREMIUMS_LIST_TEXT.allSituations,
      options: situationOptions,
    },
  };
}

/**
 * Base DataList config of the « Mes primes » list: inline rows (already
 * searched / filtered by VEOS) in API order, every row on one page,
 * client-side sort, no row click (the « Documents » button is the only row
 * action).
 */
export function buildPremiumsListConfig(
  rows: readonly PremiumListRow[],
  situationOptions: readonly SelOption[] = [],
): DataListConfig {
  return {
    id: PREMIUMS_LIST_CARD_ID,
    dataSource: { data: rows as unknown as Record<string, unknown>[] },
    columns: { ...PREMIUMS_LIST_COLUMNS },
    display: { size: 'compact', striped: true },
    sorting: { enabled: true },
    toolbar: buildListToolbar(premiumsListToolbarSpec(situationOptions)),
    pagination: { mode: 'scroll', pageSize: 999_999 },
    i18n: { emptyMessage: EMPTY_STATE_TEXT },
  };
}
